import { http } from '@/api/client/httpClient';
import type {
  CreateFaqDto,
  CreateUpdateFaqCategoryDto,
  FaqCategoryDto,
  FaqCategoryViewModel,
  FaqDto,
  UpdateFaqDto,
} from '@/api/types/faqHost';

// ABP conventional-controller mapping of Finkit.FAQ.FaqCategoryAppService
// (CrudAppService) and Finkit.FAQ.FaqAppService:
//   FaqCategory.CreateAsync                 -> POST   /api/app/faq-category
//   FaqCategory.UpdateAsync(id)             -> PUT    /api/app/faq-category/{id}
//   FaqCategory.DeleteAsync(id)             -> DELETE /api/app/faq-category/{id}
//   FaqCategory.GetListWithFaqsAsync        -> GET    /api/app/faq-category/list-with-faqs
//   FaqCategory.GetCategoryFeatureNamesAsync-> GET    /api/app/faq-category/category-feature-names?categoryId=
//   FaqCategory.UpdateCategoryFeaturesAsync -> PUT    /api/app/faq-category/category-features
//     (Update* => PUT verb, "Update" prefix stripped; CategoryId is a body field,
//      not an "id" route param. If the live route differs, this is the one to check.)
//   Faq.GetAsync(id)                        -> GET    /api/app/faq/{id}
//   Faq.CreateAsync                         -> POST   /api/app/faq
//   Faq.UpdateAsync(id)                     -> PUT    /api/app/faq/{id}
//   Faq.DeleteAsync(id)                     -> DELETE /api/app/faq/{id}
const CATEGORY_BASE = '/api/app/faq-category';
const FAQ_BASE = '/api/app/faq';

export const faqHostApi = {
  async getListWithFaqs(): Promise<FaqCategoryViewModel[]> {
    const { data } = await http.get<FaqCategoryViewModel[]>(`${CATEGORY_BASE}/with-faqs`);
    return data;
  },

  async createCategory(input: CreateUpdateFaqCategoryDto): Promise<FaqCategoryDto> {
    const { data } = await http.post<FaqCategoryDto>(CATEGORY_BASE, input);
    return data;
  },

  async updateCategory(id: string, input: CreateUpdateFaqCategoryDto): Promise<FaqCategoryDto> {
    const { data } = await http.put<FaqCategoryDto>(`${CATEGORY_BASE}/${id}`, input);
    return data;
  },

  async deleteCategory(id: string): Promise<void> {
    await http.delete(`${CATEGORY_BASE}/${id}`);
  },

  async getCategoryFeatureNames(categoryId: string): Promise<string[]> {
    const { data } = await http.get<string[]>(`${CATEGORY_BASE}/category-feature-names/${categoryId}`);
    return data;
  },

  async updateCategoryFeatures(categoryId: string, features: string[]): Promise<boolean> {
    const { data } = await http.put<boolean>(`${CATEGORY_BASE}/category-features/${categoryId}`, {
      categoryId,
      features,
    });
    return data;
  },

  async getFaq(id: string): Promise<FaqDto> {
    const { data } = await http.get<FaqDto>(`${FAQ_BASE}/${id}`);
    return data;
  },

  async createFaq(input: CreateFaqDto): Promise<FaqDto> {
    const { data } = await http.post<FaqDto>(FAQ_BASE, input);
    return data;
  },

  async updateFaq(id: string, input: UpdateFaqDto): Promise<FaqDto> {
    const { data } = await http.put<FaqDto>(`${FAQ_BASE}/${id}`, input);
    return data;
  },

  async deleteFaq(id: string): Promise<void> {
    await http.delete(`${FAQ_BASE}/${id}`);
  },
};
